import { Injectable } from '@angular/core';
import { Observable, forkJoin, fromEvent, of, timer } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ErrorService } from '../../services/error/error.service';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  constructor(private errorService: ErrorService) {}

  public printApplication(
    applicationName,
    applicationId: string,
    appendResultPage: boolean,
    includeRegulatoryPage: boolean = false
  ): Observable<any> {
    const printPages$ = this.getPrintDataByApplication(applicationName, applicationId, appendResultPage, includeRegulatoryPage);

    return forkJoin(printPages$).pipe(
      switchMap(pages => {
        const iframe = document.createElement('iframe');
        iframe.src = `src='about:blank'`;
        document.body.appendChild(iframe);

        return fromEvent(iframe, 'load').pipe(
          map(() => ({
            iframe,
            pages,
          }))
        );
      }),
      switchMap(({ iframe, pages }) => {
        iframe.contentWindow.document.body.innerHTML = '<app-root></app-root>';
        for (let index = 0; index < pages.length; index++) {
          const p = pages[index] as { head; body; iframe };
          if (!p.head && !p.body) {
            this.errorService.showError('Something went wrong, please retry or contact administrator!', 10000);
            document.body.removeChild(iframe);
            return of(null);
          } else {
            iframe.contentWindow.document.head.append(p.head);
            iframe.contentWindow.document.body.append(p.body);
            document.body.removeChild(p.iframe);
          }
        }
        return this.buildDelayedResultObservable(1000, iframe);
      }),
      switchMap(iframe => {
        if (!iframe) {
          return of(null);
        }
        iframe.contentWindow.document.body.querySelector('app-root')?.remove();
        iframe.contentWindow.print();

        return this.buildDelayedResultObservable(2000, iframe);
      }),
      switchMap(iframe => {
        if (!iframe) {
          return of(null);
        }
        document.body.removeChild(iframe);
        return of(null);
      })
    );
  }

  public generatePdfByApplication(
    applicationName,
    applicationId: string,
    appendResultPage: boolean,
    includeRegulatoryPage: boolean = false
  ): Observable<Blob> {
    const printPages$ = this.getPrintDataByApplication(applicationName, applicationId, appendResultPage, includeRegulatoryPage);

    return forkJoin(printPages$).pipe(
      switchMap(pages => {
        const iframe = document.createElement('iframe');
        iframe.src = `src='about:blank'`;
        iframe.width = '1883.94';
        document.body.appendChild(iframe);

        return fromEvent(iframe, 'load').pipe(
          map(() => ({
            iframe,
            pages,
          }))
        );
      }),
      switchMap(({ iframe, pages }) => {
        for (let index = 0; index < pages.length; index++) {
          const p = pages[index] as { head; body; iframe };
          if (!p.head && !p.body) {
            this.errorService.showError('Something went wrong, please retry or contact administrator!', 10000);
            document.body.removeChild(iframe);
            return of(null);
          } else {
            iframe.contentWindow.document.head.append(p.head);
            iframe.contentWindow.document.body.append(p.body);
            document.body.removeChild(p.iframe);
          }
        }
        return this.buildDelayedResultObservable(1000, { iframe, pages });
      }),
      switchMap(({ iframe, pages }) => {
        let canvasList = [];

        for (let index = 0; index < pages.length; index++) {
          const p = pages[index] as { head; body };
          if (p.body && p.head) {
            let style = document.createElement('style');
            style.innerHTML = 'button { display: none !important; }';
            p.head.append(style);

            this.createStyle('.inputswitch { width: 4rem !important; }', p.head);
            this.createStyle(
              '.section-wrapper { display: flex; width: 100%; background-color: #f4f5f5; padding: 1rem !important; flex-direction: column;}',
              p.head
            );
            this.createStyle('.section-column { display: flex; flex-direction: column; flex: 1; gap: 0.5rem;}', p.head);
            const switches = p.body.getElementsByClassName('ui-inputswitch');
            this.addClass(switches, 'inputswitch');

            html2canvas(p.body, { width: p.body.offsetWidth, height: p.body.offsetHeight * 1.3 }).then(canvas => {
              canvasList.push(canvas);
            });

            this.removeClass(switches, 'inputswitch');
          }
        }

        return this.buildDelayedResultObservable(2000, { iframe, canvasList });
      }),
      switchMap(({ iframe, canvasList }) => {
        if (canvasList.length === 0) {
          return of(null);
        }

        const pdf = new jsPDF({ format: [1883.94, 3370.39] });
        for (let index = 0; index < canvasList.length; index++) {
          const canvas = canvasList[index];

          pdf.addImage(canvas, 'PNG', 0, 0, canvas.width * 0.82, canvas.height, 'a' + index, 'FAST');

          if (index < canvasList.length - 1) {
            pdf.addPage();
          }
        }
        //pdf.save('file.pdf');
        const blob = pdf.output('blob');

        document.body.removeChild(iframe);
        return of(blob);
      })
    );
  }

  public generatePdfByCurrentPage(): Observable<Blob> {
    return new Observable<Blob>(observer => {
      const page = window.document.getElementsByClassName('process-details-container')[0] as HTMLElement;

      this.createStyle('.inputswitch { width: 4rem !important; }', window.document.head);
      this.createStyle('.hide-docs { display: none !important; }', window.document.head);
      this.createStyle('.field-space { margin-top: 0.625rem !important; width: 50% !important }', window.document.head);

      const switches = page.getElementsByClassName('ui-inputswitch');
      this.addClass(switches, 'inputswitch');

      const docsSection = page.getElementsByClassName('for-screen');
      this.addClass(docsSection, 'hide-docs');

      const fieldRows = page.getElementsByClassName('ui-g-6 field-rows');
      this.addClass(fieldRows, 'field-space');

      html2canvas(page, {
        width: page.offsetWidth,
        height: page.offsetHeight,
        windowHeight: page.offsetHeight,
        windowWidth: page.offsetWidth,
        scale: 1,
      }).then(canvas => {
        const pdf = new jsPDF({ format: [canvas.width, canvas.height] });

        pdf.addImage(canvas, 'PNG', 0, 0, canvas.width * 0.82, canvas.height, 'a', 'FAST');

        const blob = pdf.output('blob');

        this.removeClass(switches, 'inputswitch');
        this.removeClass(docsSection, 'hide-docs');
        this.removeClass(fieldRows, 'field-space');

        observer.next(blob);
        observer.complete();
      });
    });
  }

  private getPrintDataByApplication(
    applicationName: string,
    applicationId: string,
    appendResultPage: boolean,
    includeRegulatoryPage: boolean = false
  ): Observable<{ head: HTMLHeadElement; body: HTMLElement }>[] {
    const printPages$ = [
      this.getPrintDataFromPage(`/${applicationName}/technical-data/${applicationId}`),
      this.getPrintDataFromPage(`/${applicationName}/personal-data/${applicationId}`),
      this.getPrintDataFromPage(`/${applicationName}/documents/${applicationId}`, !appendResultPage),
    ];

    if (appendResultPage) {
      printPages$.push(this.getPrintDataFromPage(`/${applicationName}/result/${applicationId}`, true));
    }
    if (includeRegulatoryPage) {
      printPages$.splice(1, 0, this.getPrintDataFromPage(`/${applicationName}/regulatory-data/${applicationId}`));
    }

    return printPages$;
  }

  private getPrintDataFromPage(url: string, keepHistoryLogs = false): Observable<{ head: HTMLHeadElement; body: HTMLElement }> {
    const iframe = document.createElement('iframe');
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.setAttribute('hidden', 'true');

    return fromEvent(iframe, 'load').pipe(
      take(1),
      switchMap(() => {
        iframe.contentWindow.onafterprint = function () {
          document.body.removeChild(iframe);
        };

        return this.buildDelayedResultObservable(3000);
      }),
      switchMap(() => {
        const printFormElement = iframe.contentWindow.document.getElementById('print-form');
        if (!printFormElement) {
          return this.buildEmptyResultObservable(iframe);
        }

        this.enableSectionsForPrint(printFormElement);
        this.processHistoryLogsForPrint(printFormElement, keepHistoryLogs);

        const head = iframe.contentDocument.head;
        return this.buildDelayedResultObservable(500, { head, body: printFormElement, iframe: iframe });
      })
    );
  }

  // remove disabled-section class from all dom elements
  private enableSectionsForPrint(el: HTMLElement) {
    el.querySelectorAll('.disable-section').forEach(div => {
      div.classList.remove('disable-section');
    });
  }

  // remove history logs element or expand all rows there
  private processHistoryLogsForPrint(el: HTMLElement, keepHistoryLogs: boolean) {
    if (!keepHistoryLogs) {
      el.querySelector('app-order-history')?.parentElement?.removeChild(el.querySelector('app-order-history'));
      return;
    }

    // change logs to 100 rows
    var paginator = el.querySelector('app-order-history').querySelector('p-paginator');
    (paginator.querySelector('p-dropdown').querySelector('.ui-dropdown-trigger') as HTMLButtonElement).click();
    var dropdownItems = paginator.querySelectorAll('p-dropdownitem');
    dropdownItems[dropdownItems.length - 1].querySelector('li').click();

    // expand all rows
    el.querySelector('app-order-history')
      .querySelectorAll('.ui-accordion-header')
      .forEach(div => {
        (div.childNodes[0] as HTMLAnchorElement).click();
      });
  }

  private buildDelayedResultObservable<TData>(delay: number, data?: TData): Observable<TData> {
    return timer(delay).pipe(
      take(1),
      map(() => data)
    );
  }

  private buildEmptyResultObservable(iframe) {
    return of({ head: null, body: null, iframe: iframe });
  }

  private createStyle(styleInnerHTML: string, head: HTMLHeadElement) {
    let style = document.createElement('style');
    style.innerHTML = styleInnerHTML;
    head.append(style);
  }

  private addClass(elements: HTMLCollectionOf<Element>, classToAdd: string) {
    Array.from(elements).forEach(e => {
      e.classList.add(classToAdd);
    });
  }

  private removeClass(elements: HTMLCollectionOf<Element>, classToRemove: string) {
    Array.from(elements).forEach(e => {
      e.classList.remove(classToRemove);
    });
  }
}
